import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Upload, X, ArrowLeft, Image as ImageIcon, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Profile() {
    const { user, setUser, logout } = useAuth();
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    const [delPassword, setDelPassword] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [form, setForm] = useState({
        name: user?.name || "",
        company: user?.company || "",
        phone: user?.phone || "",
        marketing_email: user?.marketing_email || "",
    });
    const [logoUrl, setLogoUrl] = useState(user?.logo_url || "");
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onLogoUpload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setLogoUrl(data.url);
            toast.success("Logo geüpload");
        } catch {
            toast.error("Upload mislukt");
        }
        setUploading(false);
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch("/auth/profile", { ...form, logo_url: logoUrl || null });
            setUser(data);
            toast.success("Profiel bijgewerkt");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fout");
        }
        setSaving(false);
    };

    const logoIsImage = logoUrl && !logoUrl.toLowerCase().endsWith(".pdf");

    const downloadExport = async () => {
        try {
            const res = await api.get("/me/data-export", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "mijn_gegevens_vastgoedstudio.zip";
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
        } catch {
            toast.error("Export mislukt — probeer opnieuw");
        }
    };

    const requestDeletion = async () => {
        if (!window.confirm("Ben je zeker? Je account wordt gedeactiveerd en na 30 dagen definitief verwijderd.")) return;
        setDeleting(true);
        try {
            await api.post("/auth/delete-account", { password: delPassword });
            toast.success("Verwijdering aangevraagd — je account wordt over 30 dagen definitief verwijderd.");
            setTimeout(() => logout(), 2500);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Verwijderen mislukt");
        }
        setDeleting(false);
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 font-body pb-20" data-testid="profile-page">
            <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-10">
                <Link to={user?.role === "admin" ? "/admin" : "/dashboard"} className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950 mb-6" data-testid="profile-back">
                    <ArrowLeft className="h-4 w-4" /> Terug naar dashboard
                </Link>

                <div className="mb-10">
                    <div className="text-xs uppercase tracking-[0.3em] text-[#E5A93D] font-semibold mb-3">Profiel</div>
                    <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">Uw gegevens.</h1>
                </div>

                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
                    {/* Logo — only useful for makelaars whose logo appears in delivered videos */}
                    {user?.role !== "admin" && (
                    <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8">
                        <h2 className="font-display font-bold text-xl tracking-tight mb-2">Uw logo</h2>
                        <p className="text-sm text-zinc-500 mb-6">
                            Dit logo wordt gebruikt als branding in uw video's. Ondersteunde formaten: <span className="text-zinc-950">PNG, SVG, vector of PDF</span>. Transparante achtergrond wordt aanbevolen.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start">
                            <div className="h-32 w-32 bg-zinc-100 border border-zinc-200 rounded-md flex items-center justify-center overflow-hidden" data-testid="logo-preview">
                                {logoUrl ? (
                                    logoIsImage ? (
                                        <img src={logoUrl.startsWith("http") ? logoUrl : `${BACKEND}${logoUrl}`} alt="logo" className="max-h-full max-w-full object-contain p-2" />
                                    ) : (
                                        <div className="flex flex-col items-center gap-1 text-[#E5A93D]">
                                            <FileText className="h-8 w-8" />
                                            <span className="text-[10px] uppercase tracking-wider">PDF logo</span>
                                        </div>
                                    )
                                ) : (
                                    <ImageIcon className="h-8 w-8 text-zinc-400" />
                                )}
                            </div>
                            <div>
                                <label className="inline-flex items-center gap-2 px-4 py-2 bg-[#F4F4F5] hover:bg-[#F4F4F5] border border-zinc-200 rounded-sm cursor-pointer transition-colors" data-testid="logo-upload-btn">
                                    <Upload className="h-4 w-4" />
                                    <span className="text-sm">{uploading ? "Uploaden..." : logoUrl ? "Logo vervangen" : "Logo uploaden"}</span>
                                    <input type="file" className="hidden" accept="image/png,image/svg+xml,image/*,application/pdf,.ai,.eps" onChange={onLogoUpload} />
                                </label>
                                {logoUrl && (
                                    <button onClick={() => setLogoUrl("")} className="ml-3 text-sm text-zinc-500 hover:text-red-600 inline-flex items-center gap-1" data-testid="logo-remove-btn">
                                        <X className="h-3 w-3" /> Verwijderen
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Info */}
                    <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8">
                        <h2 className="font-display font-bold text-xl tracking-tight mb-6">Contactgegevens</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Naam</Label>
                                <Input value={form.name} onChange={set("name")} className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] h-11" data-testid="profile-name" />
                            </div>
                            {user?.role !== "admin" && (
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Kantoor</Label>
                                <Input value={form.company} onChange={set("company")} className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] h-11" data-testid="profile-company" />
                            </div>
                            )}
                            <div className="md:col-span-2">
                                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">GSM</Label>
                                <Input value={form.phone} onChange={set("phone")} className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] h-11" data-testid="profile-phone" />
                            </div>
                            {user?.role !== "admin" && (
                            <div className="md:col-span-2">
                                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Marketing email (voor opleveringen)</Label>
                                <p className="text-[11px] text-zinc-500 mb-2">Naar dit adres sturen we een melding zodra een video is opgeleverd.</p>
                                <Input type="email" value={form.marketing_email} onChange={set("marketing_email")} placeholder="bv. marketing@kantoor.be" className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D] h-11" data-testid="profile-marketing-email" />
                            </div>
                            )}
                            <div className="md:col-span-2">
                                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Email (niet wijzigbaar)</Label>
                                <Input value={user?.email || ""} disabled className="bg-[#FFFFFF] border-zinc-100 rounded-sm h-11 text-zinc-500" />
                            </div>
                        </div>
                    </div>

                    <Button onClick={save} disabled={saving} className="h-11 px-8 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98]" data-testid="profile-save-btn">
                        {saving ? "Opslaan..." : "Wijzigingen opslaan"}
                    </Button>

                    {/* GDPR: data-export (inzagerecht) */}
                    <div className="bg-[#FFFFFF] border border-zinc-200 rounded-md p-6 lg:p-8" data-testid="data-export-section">
                        <h2 className="font-display font-bold text-xl tracking-tight mb-2">Mijn gegevens</h2>
                        <p className="text-sm text-zinc-500 mb-4">Download al je gegevens (profiel, projecten, AI-generaties, credit-transacties) als ZIP met leesbare JSON-bestanden.</p>
                        <Button onClick={downloadExport} variant="outline" className="h-11 px-6 border-zinc-300 text-zinc-950 hover:bg-zinc-100 hover:text-zinc-950 font-bold rounded-sm" data-testid="data-export-btn">
                            Download mijn gegevens (ZIP)
                        </Button>
                    </div>

                    {/* GDPR: self-service accountverwijdering */}
                    {user?.role !== "admin" && (
                    <div className="bg-[#FFFFFF] border border-red-200 rounded-md p-6 lg:p-8" data-testid="danger-zone">
                        <h2 className="font-display font-bold text-xl tracking-tight mb-2 text-red-600">Account verwijderen</h2>
                        <p className="text-sm text-zinc-500 mb-5">
                            Je account wordt gedeactiveerd en na <span className="text-zinc-950 font-semibold">30 dagen definitief verwijderd</span>, inclusief je persoonsgegevens.
                            Resterende credits vervallen. Bedenk je je binnen die 30 dagen? Mail dan naar <a href="mailto:info@social-factory.be" className="text-[#E5A93D] hover:underline">info@social-factory.be</a>.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                            <div className="flex-1 max-w-xs">
                                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Bevestig met je wachtwoord</Label>
                                <Input type="password" value={delPassword} onChange={(e) => setDelPassword(e.target.value)} className="bg-[#F4F4F5] border-zinc-200 rounded-sm h-11" data-testid="delete-account-password" />
                            </div>
                            <Button onClick={requestDeletion} disabled={deleting || !delPassword} className="h-11 px-6 bg-red-600 hover:bg-red-700 text-white font-bold rounded-sm" data-testid="delete-account-btn">
                                {deleting ? "Bezig..." : "Account verwijderen"}
                            </Button>
                        </div>
                    </div>
                    )}
                </motion.div>
            </div>
        </div>
    );
}
